import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { SignOut, House as HouseIcon, ArrowRight, ArrowsClockwise, Warning, CheckCircle, ChatCircle, Buildings, Lightning, Funnel } from '@phosphor-icons/react';
import { API } from '../context/AuthContext';
import { toast } from 'sonner';
import EmptyState from '../components/EmptyState';

/**
 * Phase 2.5a — Builder dashboard.
 *
 * Lean by design (Decision 3a): a list of linked properties with their
 * outstanding/approved/rejected counts. Clicking a row opens the existing
 * /shared/<short_id> portal so we re-use 100% of the per-property UX
 * we built in Phase 1.5/1.7.
 */
export default function BuilderDashboard() {
    const [builder, setBuilder] = useState(null);
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [filter, setFilter] = useState('all'); // all | attention
    const navigate = useNavigate();

    const loadAll = async (withSpinner = true) => {
        if (withSpinner) setLoading(true); else setRefreshing(true);
        try {
            const [meRes, propRes] = await Promise.all([
                axios.get(`${API}/builder/me`, { withCredentials: true }),
                axios.get(`${API}/builder/properties`, { withCredentials: true }),
            ]);
            setBuilder(meRes.data?.builder || null);
            setProperties(propRes.data?.properties || []);
        } catch (err) {
            if (err?.response?.status === 401) {
                navigate('/builder/login', { replace: true });
                return;
            }
            toast.error('Could not load your dashboard. Try again in a moment.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => { loadAll(true); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    // Cross-property roll-ups for the summary tiles. All defined regardless
    // of properties.length so the hooks order stays stable across renders.
    const totals = useMemo(() => {
        return properties.reduce((acc, p) => {
            const s = p.stats || {};
            acc.outstanding += s.outstanding || 0;
            acc.awaiting += s.awaiting_response || 0;
            acc.disputed += s.disputed || 0;
            acc.urgent += s.urgent_outstanding || 0;
            return acc;
        }, { outstanding: 0, awaiting: 0, disputed: 0, urgent: 0 });
    }, [properties]);

    const needsAttention = (p) => {
        const s = p.stats || {};
        return (s.awaiting_response || 0) > 0
            || (s.disputed || 0) > 0
            || (s.urgent_outstanding || 0) > 0;
    };

    const visibleProperties = useMemo(() => {
        if (filter === 'attention') return properties.filter(needsAttention);
        return properties;
    }, [properties, filter]);

    const attentionCount = useMemo(() => properties.filter(needsAttention).length, [properties]);

    const logout = async () => {
        try { await axios.post(`${API}/builder/auth/logout`, {}, { withCredentials: true }); } catch { /* noop */ }
        navigate('/builder/login', { replace: true });
    };

    const openProperty = async (propId) => {
        try {
            const r = await axios.get(`${API}/builder/property/${propId}/share-key`, { withCredentials: true });
            const sid = r.data?.short_id;
            const replyKey = r.data?.reply_key;
            if (!sid) {
                toast.error('No share link yet — ask your homeowner to generate one.');
                return;
            }
            navigate(`/shared/${sid}${replyKey ? `?r=${encodeURIComponent(replyKey)}` : ''}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not open this property.');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
                <img src="/defectr-logo.png" alt="DefectR" className="h-10 w-auto animate-pulse" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FAFAFA]">
            <header className="border-b border-[#18181B] bg-white px-4 sm:px-6 py-3 flex items-center justify-between">
                <Link to="/builder/dashboard" className="flex items-center gap-2 no-underline">
                    <img src="/defectr-logo.png" alt="DefectR" className="h-7 w-auto" />
                    <span className="hidden sm:inline-block ml-2 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] bg-[#E8F5FB] text-[#1787AB] border border-[#1FA5CF]">Builder</span>
                </Link>
                <div className="flex items-center gap-3">
                    {builder?.email && (
                        <div className="hidden sm:block text-xs text-[#52525B] font-mono uppercase tracking-wider">
                            {builder.email}
                        </div>
                    )}
                    <button
                        onClick={logout}
                        data-testid="builder-logout-btn"
                        className="inline-flex items-center gap-1 text-xs text-[#52525B] hover:text-[#E53935] font-mono uppercase tracking-wider"
                    >
                        <SignOut size={14} weight="bold" /> Sign out
                    </button>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="builder-dashboard">
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <div className="label-tiny mb-1">Your linked plots</div>
                        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                            {properties.length === 0
                                ? 'No plots linked yet'
                                : properties.length === 1 ? '1 plot' : `${properties.length} plots`}
                        </h1>
                    </div>
                    <button
                        onClick={() => loadAll(false)}
                        disabled={refreshing}
                        data-testid="builder-refresh-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#E4E4E7] hover:border-[#18181B] font-mono text-[10px] uppercase tracking-[0.18em] disabled:opacity-50"
                    >
                        <ArrowsClockwise size={12} weight="bold" className={refreshing ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                </div>

                {properties.length === 0 ? (
                    <EmptyState
                        icon={<Buildings size={36} weight="duotone" color="#1FA5CF" />}
                        title="No homeowners have linked you to a property yet"
                        body="Ask your homeowner to invite this email address from their property page in DefectR. Once they do, the plot will show up here automatically — no need to refresh."
                    />
                ) : (
                    <>
                        {/* Cross-property summary — at-a-glance triage */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6" data-testid="builder-summary">
                            <SummaryTile
                                label="Outstanding"
                                value={totals.outstanding}
                                color="#E53935"
                                icon={<Warning size={14} weight="fill" />}
                                testId="summary-outstanding"
                            />
                            <SummaryTile
                                label="Awaiting you"
                                value={totals.awaiting}
                                color="#1787AB"
                                icon={<ChatCircle size={14} weight="fill" />}
                                emphasis
                                testId="summary-awaiting"
                            />
                            <SummaryTile
                                label="Urgent"
                                value={totals.urgent}
                                color="#991B1B"
                                icon={<Lightning size={14} weight="fill" />}
                                testId="summary-urgent"
                            />
                            <SummaryTile
                                label="Disputed"
                                value={totals.disputed}
                                color="#1E3A8A"
                                icon={<ChatCircle size={14} weight="fill" />}
                                testId="summary-disputed"
                            />
                        </div>

                        {/* Filter chips — quick triage */}
                        <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="builder-filter">
                            <Funnel size={14} weight="bold" className="text-[#52525B]" />
                            <FilterChip
                                active={filter === 'all'}
                                onClick={() => setFilter('all')}
                                testId="filter-all"
                            >
                                All ({properties.length})
                            </FilterChip>
                            <FilterChip
                                active={filter === 'attention'}
                                onClick={() => setFilter('attention')}
                                testId="filter-attention"
                                accent
                            >
                                Needs attention ({attentionCount})
                            </FilterChip>
                        </div>

                        {visibleProperties.length === 0 ? (
                            <div className="border border-dashed border-[#E4E4E7] p-6 text-center text-sm text-[#52525B]" data-testid="builder-filter-empty">
                                Nothing needs attention right now. Nice work.
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {visibleProperties.map(p => {
                                    const stats = p.stats || {};
                                    return (
                                        <button
                                            key={p.id}
                                            onClick={() => openProperty(p.id)}
                                            data-testid={`builder-property-${p.id}`}
                                            className="w-full text-left bg-white border border-[#E4E4E7] hover:border-[#18181B] p-4 active:scale-[0.99] transition-all duration-150 group"
                                        >
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex items-start gap-3 min-w-0 flex-1">
                                                    <HouseIcon size={20} weight="duotone" color="#1FA5CF" className="flex-shrink-0 mt-0.5" />
                                                    <div className="min-w-0 flex-1">
                                                        <div className="font-display font-semibold truncate">{p.name}</div>
                                                        <div className="text-xs text-[#52525B] truncate">
                                                            {p.address || '—'}
                                                            {p.developer_name && <span className="ml-1">· {p.developer_name}</span>}
                                                        </div>
                                                    </div>
                                                </div>
                                                <ArrowRight size={16} weight="bold" className="text-[#A1A1AA] group-hover:text-[#1FA5CF] flex-shrink-0 mt-1" />
                                            </div>

                                            <div className="mt-3 flex flex-wrap gap-3 pl-7 text-[10px] font-mono uppercase tracking-wider">
                                                <span className="inline-flex items-center gap-1 text-[#E53935]">
                                                    <Warning size={12} weight="fill" /> {stats.outstanding ?? 0} outstanding
                                                </span>
                                                {(stats.awaiting_response ?? 0) > 0 && (
                                                    <span className="inline-flex items-center gap-1 text-[#1787AB] font-bold">
                                                        <ChatCircle size={12} weight="fill" /> {stats.awaiting_response} awaiting you
                                                    </span>
                                                )}
                                                {(stats.urgent_outstanding ?? 0) > 0 && (
                                                    <span className="inline-flex items-center gap-1 text-[#991B1B]">
                                                        <Lightning size={12} weight="fill" /> {stats.urgent_outstanding} urgent
                                                    </span>
                                                )}
                                                <span className="inline-flex items-center gap-1 text-[#1787AB]">
                                                    <CheckCircle size={12} weight="fill" /> {stats.approved ?? 0} approved
                                                </span>
                                                {(stats.rejected ?? 0) > 0 && (
                                                    <span className="inline-flex items-center gap-1 text-[#B45309]">
                                                        {stats.rejected} rejected
                                                    </span>
                                                )}
                                                {(stats.disputed ?? 0) > 0 && (
                                                    <span className="inline-flex items-center gap-1 text-[#1E3A8A]">
                                                        <ChatCircle size={12} weight="fill" /> {stats.disputed} disputed
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}

                <p className="mt-12 text-center text-[10px] font-mono uppercase tracking-[0.2em] text-[#A1A1AA]">
                    Plots arrive here automatically when homeowners invite you
                </p>
            </main>
        </div>
    );
}

function SummaryTile({ label, value, color, icon, emphasis = false, testId }) {
    return (
        <div
            data-testid={testId}
            className={`border p-3 bg-white transition-all duration-150 ${emphasis && value > 0 ? 'border-[#18181B]' : 'border-[#E4E4E7]'}`}
        >
            <div className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-[0.18em] text-[#52525B]">
                <span style={{ color }}>{icon}</span>
                {label}
            </div>
            <div
                className="font-display text-2xl sm:text-3xl font-bold mt-1 tabular-nums"
                style={{ color: value > 0 ? color : '#A1A1AA' }}
            >
                {value}
            </div>
        </div>
    );
}

function FilterChip({ active, onClick, children, testId, accent = false }) {
    let base = 'px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] border transition-all duration-150';
    let cls;
    if (active) {
        cls = accent ? 'bg-[#991B1B] text-white border-[#991B1B]' : 'bg-[#18181B] text-white border-[#18181B]';
    } else {
        cls = 'bg-white text-[#09090B] border-[#E4E4E7] hover:border-[#18181B]';
    }
    return (
        <button onClick={onClick} data-testid={testId} className={`${base} ${cls}`}>
            {children}
        </button>
    );
}
